import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "CustomWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Specialty Woven Mesh",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Custom Woven Wire Mesh",
      lead: "Bespoke wire cloth engineering configuring non-standard warp-to-shute ratios, specialized alloy selections, and tailored edge constructions for OEM equipment integration.",
      secondaryCta: {
        label: "Specialty Woven Mesh",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Precision custom engineered stainless steel woven wire mesh weave structure",
      },
      cardNote: "OEM Application Engineering",
      cardLink: {
        label: "Submit Drawing or Spec",
        to: "/ContactPage",
      },
      specs: [
        { label: "Aperture Span", value: "1 µm – 25 mm" },
        { label: "Mesh Count", value: "1 – 635 Mesh" },
        { label: "Wire Diameter", value: "0.020 – 2.0 mm" },
        { label: "Max Roll Width", value: "Up to 3,000 mm" },
      ],
    },
    overview: {
      eyebrow: "Tailored Manufacturing",
      heading: "Custom Engineering from Loom Setup to Finished Filtration Component",
      paragraphs: [
        "Standard catalog mesh specifications do not always fulfill the exact mechanical, hydrodynamic, or spatial requirements of specialized process equipment. Our custom weaving program provides engineers with direct control over wire cloth parameters, enabling tailored wire diameter pairings, rectangular aperture ratios, and selective edge finishing.",
        "Whether adjusting the warp-to-shute ratio to reinforce tensile strength along the machine direction or specifying proprietary aperture dimensions to optimize fluid residence time, our weaving looms are set up to precise dimensional tolerances in accordance with ASTM E2016 and ISO 9044 guidelines.",
        "Beyond roll goods, custom wire mesh orders are supported by downstream fabrication services including precision rotary slitting, laser cutting, stamping of circular filter discs, multi-layer spot welding, and custom metallic border framing ready for direct system assembly.",
      ],
      keyPoints: [
        "Custom warp-to-shute wire ratios providing anisotropic tensile reinforcement and targeted fluid conductance",
        "Woven from verified stainless steel alloys including AISI 304, 316, and low-carbon 316L",
        "Full downstream conversion including precision slitting, stamped shapes, pleated packs, and framed edges",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Custom stainless steel wire cloth roll inspection and precision aperture verification",
      },
    },
    specTable: {
      heading: "Custom Engineering Parameter Ranges",
      intro: "Manufacturing limits and dimensional ranges available for bespoke woven wire mesh orders.",
      columns: [
        { key: "parameter", label: "Engineering Parameter", align: "left" },
        { key: "customRange", label: "Available Custom Range", align: "right" },
        { key: "toleranceStd", label: "Manufacturing Standard", align: "left" },
        { key: "options", label: "Design Capabilities", align: "left" },
      ],
      rows: [
        {
          parameter: "Aperture Opening",
          customRange: "1 µm – 25 mm",
          toleranceStd: "ISO 9044 / ASTM E2016",
          options: "Square openings, rectangular slots, or tailored micron limits",
        },
        {
          parameter: "Mesh Count",
          customRange: "1 – 635 Mesh",
          toleranceStd: "ASTM E2016",
          options: "Balanced square weave or non-uniform warp/shute counts",
        },
        {
          parameter: "Wire Diameter",
          customRange: "0.020 – 2.0 mm",
          toleranceStd: "DIN 4189 / ASTM A580",
          options: "Standard austenitic grades 304, 316, 316L",
        },
        {
          parameter: "Weaving Width",
          customRange: "Up to 3,000 mm",
          toleranceStd: "Factory Standard",
          options: "Narrow slit ribbons from 10 mm up to wide 3,000 mm master rolls",
        },
      ],
      caption: "Material test reports and PMI inspection records available when specified in the order.",
    },
    features: {
      heading: "Engineering & Converting Capabilities",
      intro: "Full-spectrum manufacturing capabilities supporting custom wire cloth integration.",
      items: [
        {
          title: "Anisotropic Reinforcement",
          text: "Configuring heavier warp wires provides increased tensile strength in high-pull belt filters without restricting transversal opening size.",
        },
        {
          title: "Custom Edge Finishes",
          text: "Choice of clean-cut edges, ultrasonic slitting, melted selvages, or welded border tapes to prevent edge fraying during installation.",
        },
        {
          title: "Specialty Stamping & Blanking",
          text: "Precision dies produce single- or multi-layer discs, donuts, and irregular profiles with clean, burr-free peripheral edges.",
        },
        {
          title: "Joined Filter Assemblies",
          text: "In-house resistance welding, seam rolling, and framing convert custom cloth into ready-to-mount cartridges and screen baskets.",
        },
      ],
    },
    related: {
      heading: "Related Specialty Weaves & Sibling Architectures",
      intro: "Explore how custom woven mesh compares with standard specialty architectures in our catalog.",
      cards: [
        {
          title: "Specialty Woven Mesh",
          text: "Review the comprehensive family of engineered wire cloth products developed for critical process environments.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Parent Category",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Specialty precision stainless steel woven wire mesh",
          },
        },
        {
          title: "Five-Heddle Weave",
          text: "1/4 satin weave offering a planar low-friction surface for rapid cake release in pressure leaf filtration.",
          to: "/FiveHeddleWeavePage",
          badge: "Cake Release",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Five-heddle stainless steel woven wire mesh structure",
          },
        },
        {
          title: "Heavy-Duty Woven Mesh",
          text: "Rigid pre-crimped screens engineered from heavy-gauge wire for aggregate sizing and severe mechanical abrasion.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Heavy Gauge",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Industrial heavy-duty pre-crimped woven wire mesh",
          },
        },
      ],
    },
    cta: {
      id: "custom-woven-cta",
      eyebrow: "OEM Engineering Collaboration",
      heading: "Configure Your Custom Woven Wire Mesh",
      lead: "Submit your operating specifications, dimensional drawings, or sample requirements to receive engineering feedback and formal quotation.",
      secondaryLabel: "Specialty Weaves Overview",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Prototypes, custom slit coils, and production roll runs available upon request.",
    },
  },

  zh: {
    breadcrumbKey: "CustomWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "特种编织丝网",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "定制编织丝网",
      lead: "根据主机设备装配与工艺工况要求，定制非标经纬配比、特殊开孔几何形状、开孔公差及深加工组件方案。",
      secondaryCta: {
        label: "特种编织网总览",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "高精度定制不锈钢编织丝网结构",
      },
      cardNote: "OEM 专属工程定制",
      cardLink: {
        label: "提交图纸或参数",
        to: "/ContactPage",
      },
      specs: [
        { label: "孔径跨度", value: "1 µm – 25 mm" },
        { label: "目数范围", value: "1 – 635 目" },
        { label: "丝径范围", value: "0.020 – 2.0 mm" },
        { label: "最大幅宽", value: "最高 3,000 mm" },
      ],
    },
    overview: {
      eyebrow: "工程定制织造",
      heading: "从精密织机调试到成品过滤组件的全流程非标定制",
      paragraphs: [
        "标准样本目数往往难以完全匹配特定工艺装备在机械张力、流体阻力或特殊安装空间的严苛要求。我们的定制织网工艺允许技术工程师直接调整经纬丝径配比、设计长方孔等非标开孔形态，并指定专属的边缘锁边处理。",
        "无论是通过加大经丝丝径提升带滤机或滤网带的抗拉抗扯强度，还是优化孔径开孔率以满足特定的流体停留时间与压降平衡，我们的工业织机均严格遵循 ASTM E2016 与 ISO 9044 国际公差标准进行参数标定与试织。",
        "除标准原卷外，我们还提供完善的后道深加工制造服务：包括高精度微米级分条、激光切割下料、冲压多层包边滤片、点焊成型滤筒以及装配金属保护骨架，直接交付可上机装配的成品部件。",
      ],
      keyPoints: [
        "支持非对称经纬丝径定制，定向强化运行机械抗拉强度并优化流道特性",
        "选用经成分检验的 AISI 304、316 及 316L 优质奥氏体不锈钢线材",
        "提供高精度精密分条、冲压成形、多层叠压焊接及金属包边等深加工服务",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "定制不锈钢丝网出厂卷料质检与孔径微观核验",
      },
    },
    specTable: {
      heading: "定制工程制造参数范围",
      intro: "非标定制不锈钢编织丝网的核心制造能力与参数范围。",
      columns: [
        { key: "parameter", label: "制造技术参数", align: "left" },
        { key: "customRange", label: "可定制范围", align: "right" },
        { key: "toleranceStd", label: "执行公差标准", align: "left" },
        { key: "options", label: "工艺特点", align: "left" },
      ],
      rows: [
        {
          parameter: "开孔孔径范围",
          customRange: "1 µm – 25 mm",
          toleranceStd: "ISO 9044 / ASTM E2016",
          options: "正方孔、矩形长孔或非对称微米级孔径",
        },
        {
          parameter: "目数配置",
          customRange: "1 – 635 目",
          toleranceStd: "ASTM E2016",
          options: "方孔平衡编织或非等目经纬织造",
        },
        {
          parameter: "金属丝径范围",
          customRange: "0.020 – 2.0 mm",
          toleranceStd: "DIN 4189 / ASTM A580",
          options: "304, 316, 316L 优质奥氏体不锈钢",
        },
        {
          parameter: "最大编织幅宽",
          customRange: "最高 3,000 mm",
          toleranceStd: "出厂制造公差",
          options: "支持 10 mm 窄条分卷至 3,000 mm 宽幅原卷",
        },
      ],
      caption: "材质报告 (MTR) 与 PMI 光谱材质复核可按订单技术要求核验提供。",
    },
    features: {
      heading: "工程设计与深加工转化能力",
      intro: "为 OEM 装备制造商量身打造的技术配套优势。",
      items: [
        {
          title: "各向异性抗拉强化",
          text: "针对带式过滤机与高拉力拖动工况，采用强化经丝配比，提升抗撕裂性能而不影响截留精度。",
        },
        {
          title: "多样化边缘处理",
          text: "提供毛边裁切、超声波分切、自然织边及焊接包边封固，杜绝安装与运行中的散丝脱丝风险。",
        },
        {
          title: "精密冲压与成形",
          text: "采用精密级进冲模制作单层或多层圆片、环片及异形过滤元件，边缘平整无毛刺。",
        },
        {
          title: "焊接与结构件组装",
          text: "具备电阻缝焊、点焊及滚圆加工能力，将定制丝网快速加工为成品滤筒、滤篮与滤板滤芯。",
        },
      ],
    },
    related: {
      heading: "相关特种编织结构与同级产品",
      intro: "对比定制编织网与已有标准特种编织结构，确定最佳工程路线。",
      cards: [
        {
          title: "特种编织丝网",
          text: "了解特种编织丝网完整分类，涵盖缎纹网、重型轧花网及工程定制编织网。",
          to: "/SpecialtyWovenMeshPage",
          badge: "上级分类",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "特种精密不锈钢编织丝网",
          },
        },
        {
          title: "五综编织丝网",
          text: "1/4 斜缎纹织造，单面平整光滑利于滤饼彻底剥离，适用于叶滤机与固液脱水过滤。",
          to: "/FiveHeddleWeavePage",
          badge: "滤饼剥离",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "五综不锈钢编织丝网微观织造结构示意图",
          },
        },
        {
          title: "重型编织网",
          text: "采用粗线径预弯成型定型结构，专为矿业、骨料筛分等重载磨损环境设计。",
          to: "/HeavyDutyWovenMeshPage",
          badge: "重载高刚度",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "工业重型不锈钢预弯轧花编织丝网",
          },
        },
      ],
    },
    cta: {
      id: "custom-woven-cta",
      eyebrow: "OEM 工程技术合作",
      heading: "定制专属规格不锈钢编织丝网方案",
      lead: "欢迎发送您的工况介质条件、装配图纸或样品要求，我们的技术团队将在核验工艺可行性后提供详细方案。",
      secondaryLabel: "返回特种编织总览",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "支持从小批量试样试织到规模化母卷量产全阶段技术配合。",
    },
  },

  es: {
    breadcrumbKey: "CustomWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Malla de alambre especial",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Malla tejida a medida",
      lead: "Ingeniería de tela metálica personalizada con relaciones no estándar entre urdimbre y trama, aleaciones seleccionadas y confección a medida para integración OEM.",
      secondaryCta: {
        label: "Malla especial general",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Estructura de tejido de malla de acero inoxidable a medida",
      },
      cardNote: "Ingeniería para fabricantes OEM",
      cardLink: {
        label: "Enviar plano o datos",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango de abertura", value: "1 µm – 25 mm" },
        { label: "Conteo de malla", value: "1 – 635 Mesh" },
        { label: "Diámetro de alambre", value: "0.020 – 2.0 mm" },
        { label: "Ancho máximo", value: "Hasta 3.000 mm" },
      ],
    },
    overview: {
      eyebrow: "Fabricación a medida",
      heading: "Ingeniería personalizada: desde la preparación del telar hasta el componente final",
      paragraphs: [
        "Las especificaciones de catálogo estándar no siempre satisfacen los requerimientos mecánicos, hidráulicos o dimensionales de equipos de proceso complejos. Nuestro programa de tejido a medida ofrece control directo sobre el diámetro de los hilos, aberturas rectangulares y acabados de orillo.",
        "Ya sea para reforzar la resistencia a la tracción longitudinal en filtros de banda mediante alambres de urdimbre más gruesos o para definir aberturas específicas que optimicen el tiempo de retención, nuestros telares se ajustan a tolerancias rigurosas según ASTM E2016 e ISO 9044.",
        "Además de rollos maestros, nuestros pedidos a medida cuentan con servicios de corte longitudinal de precisión, corte por láser, troquelado de discos circulares, soldadura por puntos multicapa y remate con marcos metálicos listos para montaje.",
      ],
      keyPoints: [
        "Relaciones personalizadas urdimbre/trama que proporcionan refuerzo mecánico direccional",
        "Fabricación en aceros inoxidables austeníticos certificados AISI 304, 316 y 316L",
        "Servicios integrales de acabado: corte en tiras, troquelado de formas y conjuntos soldados",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Inspección de rollo de malla tejida a medida y verificación de luz de malla",
      },
    },
    specTable: {
      heading: "Rangos de fabricación a medida",
      intro: "Límites dimensionales y parámetros técnicos disponibles para pedidos personalizados.",
      columns: [
        { key: "parameter", label: "Parámetro técnico", align: "left" },
        { key: "customRange", label: "Rango disponible", align: "right" },
        { key: "toleranceStd", label: "Norma de tolerancias", align: "left" },
        { key: "options", label: "Capacidad de diseño", align: "left" },
      ],
      rows: [
        {
          parameter: "Abertura libre",
          customRange: "1 µm – 25 mm",
          toleranceStd: "ISO 9044 / ASTM E2016",
          options: "Cuadrada, rectangular o límites micrométricos específicos",
        },
        {
          parameter: "Conteo de malla",
          customRange: "1 – 635 Mesh",
          toleranceStd: "ASTM E2016",
          options: "Tejido cuadrado equilibrado o conteos asimétricos",
        },
        {
          parameter: "Diámetro de alambre",
          customRange: "0.020 – 2.0 mm",
          toleranceStd: "DIN 4189 / ASTM A580",
          options: "Aceros inoxidables AISI 304, 316 y 316L",
        },
        {
          parameter: "Ancho de tejido",
          customRange: "Hasta 3.000 mm",
          toleranceStd: "Estándar de planta",
          options: "Tiras cortadas desde 10 mm hasta bobinas de 3.000 mm",
        },
      ],
      caption: "Certificados de colada e inspección PMI disponibles cuando se especifiquen en el pedido.",
    },
    features: {
      heading: "Capacidades de diseño y transformación",
      intro: "Ventajas de fabricación adaptadas a fabricantes de equipos originales.",
      items: [
        {
          title: "Refuerzo anisotrópico",
          text: "Configuración de alambres longitudinales más resistentes para soportar esfuerzos de tracción en bandas continuas.",
        },
        {
          title: "Acabados de borde protegidos",
          text: "Corte limpio, orillo soldado o ribeteado metálico para prevenir el deshilachado durante el montaje.",
        },
        {
          title: "Troquelado de precisión",
          text: "Matrices dedicadas para discos sencillos o multicapa y geometrías especiales libres de rebabas.",
        },
        {
          title: "Uniones y soldaduras",
          text: "Soldadura por resistencia y calandrado para entregar filtros cilíndricos y cestas acabadas.",
        },
      ],
    },
    related: {
      heading: "Mallas especiales y variantes relacionadas",
      intro: "Compare las opciones a medida con las tipologías estándar de nuestra gama de tejidos especiales.",
      cards: [
        {
          title: "Malla tejida especial",
          text: "Visión integral de las telas metálicas avanzadas para entornos mecánicos y químicos severos.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Categoría principal",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Malla de alambre tejida especial de acero inoxidable",
          },
        },
        {
          title: "Tejido de cinco lizos",
          text: "Tejido raso con superficie plana de baja fricción para desprendimiento rápido de torta.",
          to: "/FiveHeddleWeavePage",
          badge: "Descarga de torta",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Diagrama estructural de tejido de cinco lizos",
          },
        },
        {
          title: "Malla tejida de servicio pesado",
          text: "Cribas rígidas preonduladas para trabajo con áridos, minerales y alta abrasión mecánica.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Servicio pesado",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Malla preondulada para servicio pesado",
          },
        },
      ],
    },
    cta: {
      id: "custom-woven-cta",
      eyebrow: "Colaboración técnica OEM",
      heading: "Configurar su malla tejida a medida",
      lead: "Envíenos sus especificaciones de proceso, planos o muestras para recibir una evaluación de viabilidad y presupuesto.",
      secondaryLabel: "Ver mallas especiales",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Disponibilidad de muestras de prueba, bobinas y series completas de producción.",
    },
  },

  de: {
    breadcrumbKey: "CustomWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Spezial-Drahtgewebe",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Kundenspezifisches Drahtgewebe",
      lead: "Maßgeschneiderte Webtechnik mit individueller Abstimmung von Kette und Schuss, gezielter Werkstoffauswahl und kundenspezifischer Konfektionierung für Erstausrüster.",
      secondaryCta: {
        label: "Spezialgewebe Übersicht",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Kundenspezifische Drahtgewebestruktur aus Edelstahl unter technischer Prüfung",
      },
      cardNote: "Engineering für Erstausrüster (OEM)",
      cardLink: {
        label: "Zeichnung / Daten senden",
        to: "/ContactPage",
      },
      specs: [
        { label: "Maschenweiten", value: "1 µm – 25 mm" },
        { label: "Maschenbereich", value: "1 – 635 Mesh" },
        { label: "Drahtdurchmesser", value: "0,020 – 2,0 mm" },
        { label: "Max. Rollenbreite", value: "Bis 3.000 mm" },
      ],
    },
    overview: {
      eyebrow: "Sonderanfertigung nach Maß",
      heading: "Individuelle Webkonfiguration vom Webmaschineneinzug bis zum fertigen Filterbauteil",
      paragraphs: [
        "Katalog-Standardgewebe genügen nicht in jedem Fall den spezifischen mechanischen, strömungstechnischen oder baulichen Anforderungen moderner Prozessanlagen. Unser Programm für kundenspezifisches Gewebe ermöglicht die freie Konfiguration von Drahtstärken, Rechtecköffnungen und Randabschlüssen.",
        "Ob durch stärkere Kettfäden zur Aufnahme hoher Zugkräfte in Bandfiltern oder durch gezielt angepasste freie Siebflächen zur Regulierung von Druckverlust und Verweilzeit: Unsere Webmaschinen werden präzise nach den Vorgaben der ISO 9044 und ASTM E2016 eingerichtet.",
        "Ergänzend zu Rollenware bieten wir eine vollständige Weiterverarbeitung im Haus: Präzisionsspalten in Schmalbänder, Laserschneiden, Stanzen von Ronden, Widerstandsschweißen von Zylindern sowie das Einfassen mit Metallprofilen.",
      ],
      keyPoints: [
        "Individuelle Abstimmung von Kett- und Schussdrähten für anisotrope Zugfestigkeit und optimale Strömung",
        "Gefertigt aus geprüften austenitischen Edelstählen wie AISI 304, 316 und 316L",
        "Umfassende Konfektionierung: Schmalbänder, Stanzteile, Filterzylinder und eingefasste Siebelemente",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Qualitätsprüfung und Vermessung kundenspezifischer Drahtgeweberollen",
      },
    },
    specTable: {
      heading: "Fertigungsbereiche für kundenspezifische Gewebe",
      intro: "Mögliche Maßbereiche und technische Toleranzen für individuelle Gewebefertigungen.",
      columns: [
        { key: "parameter", label: "Parameter", align: "left" },
        { key: "customRange", label: "Fertigungsbereich", align: "right" },
        { key: "toleranceStd", label: "Fertigungsnorm", align: "left" },
        { key: "options", label: "Gestaltungsmöglichkeiten", align: "left" },
      ],
      rows: [
        {
          parameter: "Öffnungsweite",
          customRange: "1 µm – 25 mm",
          toleranceStd: "ISO 9044 / ASTM E2016",
          options: "Quadratische, rechteckige Maschen oder Mikronöffnungen",
        },
        {
          parameter: "Maschenzahl",
          customRange: "1 – 635 Mesh",
          toleranceStd: "ASTM E2016",
          options: "Quadratisch symmetrisch oder ungleiche Kett-/Schusszahlen",
        },
        {
          parameter: "Drahtdurchmesser",
          customRange: "0,020 – 2,0 mm",
          toleranceStd: "DIN 4189 / ASTM A580",
          options: "Edelstahlqualitäten AISI 304, 316 und 316L",
        },
        {
          parameter: "Webbreite",
          customRange: "Bis 3.000 mm",
          toleranceStd: "Werkstoleranz",
          options: "Spaltbänder ab 10 mm bis hin zu 3.000 mm breiten Masterrollen",
        },
      ],
      caption: "Werksprüfzeugnisse und PMI-Materialprüfungen auftragsbezogen auf Anfrage lieferbar.",
    },
    features: {
      heading: "Engineering- & Konfektionskompetenz",
      intro: "Fertigungsvorteile für Erstausrüster und Serienhersteller.",
      items: [
        {
          title: "Gezielte Festigkeitsauslegung",
          text: "Stärkere Kettfäden widerstehen hohen Zugbelastungen in Bandfiltrationsanlagen ohne Verengung der Queröffnungen.",
        },
        {
          title: "Sichere Kantenausführung",
          text: "Präzisionsschnitt, Ultraschalltrennung oder eingefasste Blechprofile verhindern das Ausfransen bei der Montage.",
        },
        {
          title: "Präzisionsstanzen",
          text: "Saubere, gratfreie Fertigung von Ronden, Ringen und geometrischen Formteilen nach Kundenzeichnung.",
        },
        {
          title: "Schweißverbindungen",
          text: "Überlapp- und Rollnahtschweißen zur Fertigung einbaufertiger Zylinder, Körbe und Filterkerzen.",
        },
      ],
    },
    related: {
      heading: "Verwandte Spezialgewebe",
      intro: "Vergleichen Sie individuelle Sondergewebe mit den Standards unserer Spezialweberei.",
      cards: [
        {
          title: "Spezial-Drahtgewebe",
          text: "Gesamtübersicht technischer Gewebekonstruktionen für anspruchsvolle Trennverfahren.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Hauptkategorie",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Präzisions-Spezialdrahtgewebe aus Edelstahl",
          },
        },
        {
          title: "Fünfschaftgewebe (Five-Heddle)",
          text: "Atlasbindung mit glatter Oberseite zur Vermeidung von Kuchenanhaftungen in Blattfiltern.",
          to: "/FiveHeddleWeavePage",
          badge: "Kuchenabwurf",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Strukturdiagramm für Fünfschaftgewebe",
          },
        },
        {
          title: "Schwerlast-Drahtgewebe",
          text: "Vorgekröpfte Drahtgewebe für den robusten Einsatz in Klassieranlagen und bei starkem Abrieb.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Schwere Drahtstärken",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Vorgekröpftes Schwerlastgewebe aus Edelstahl",
          },
        },
      ],
    },
    cta: {
      id: "custom-woven-cta",
      eyebrow: "OEM-Entwicklungspartnerschaft",
      heading: "Kundenspezifisches Drahtgewebe konfigurieren",
      lead: "Übermitteln Sie uns Ihre Betriebsdaten, Zeichnungen oder Muster für eine technische Machbarkeitsprüfung und ein Angebot.",
      secondaryLabel: "Spezialgewebe ansehen",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Musterfertigung, Schmalbänder und Serienchargen nach Kundenwunsch lieferbar.",
    },
  },

  fr: {
    breadcrumbKey: "CustomWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Toile métallique spéciale",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Toile tissée sur mesure",
      lead: "Ingénierie de toiles métalliques configurant des rapports chaîne/trame spécifiques, des alliages sélectionnés et des parachèvements adaptés aux intégrateurs OEM.",
      secondaryCta: {
        label: "Toiles spéciales générales",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Structure de tissage de toile métallique en inox sur mesure",
      },
      cardNote: "Ingénierie pour constructeurs OEM",
      cardLink: {
        label: "Soumettre plan ou paramètres",
        to: "/ContactPage",
      },
      specs: [
        { label: "Plage d'ouverture", value: "1 µm – 25 mm" },
        { label: "Nombre de mailles", value: "1 – 635 Mesh" },
        { label: "Diamètre de fil", value: "0,020 – 2,0 mm" },
        { label: "Largeur maximale", value: "Jusqu'à 3 000 mm" },
      ],
    },
    overview: {
      eyebrow: "Fabrication sur cahier des charges",
      heading: "Accompagnement complet : du réglage du métier à tisser au composant filtrant fini",
      paragraphs: [
        "Les toiles standards de catalogue ne répondent pas toujours aux contraintes mécaniques, fluidiques ou dimensionnelles de certains équipements spécifiques. Notre programme sur mesure offre aux bureaux d'études la liberté de dimensionner les diamètres de fil, les mailles rectangulaires et les lisières.",
        "Qu'il s'agisse de renforcer la résistance à la traction des toiles pour filtres à bande par des fils de chaîne de plus fort diamètre ou d'adapter les surfaces ouvertes pour équilibrer pertes de charge et temps de séjour, nos métiers sont étalonnés selon les normes ISO 9044 et ASTM E2016.",
        "Au-delà des rouleaux bruts, nos fabrications sur mesure s'appuient sur un atelier de transformation intégré : refendage de précision, découpe laser, emboutissage de disques, soudure par résistance et montage de cadres métalliques étanches.",
      ],
      keyPoints: [
        "Rapports personnalisés chaîne/trame assurant un renfort de traction directionnel",
        "Tissage en aciers inoxydables austénitiques certifiés AISI 304, 316 et 316L",
        "Atelier complet de parachèvement : refendage en bandes, découpe de pièces et éléments soudés",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Contrôle qualité et vérification optique d'une toile métallique spéciale en rouleau",
      },
    },
    specTable: {
      heading: "Capacités de fabrication sur mesure",
      intro: "Plages dimensionnelles et critères de fabrication pour toiles métalliques personnalisées.",
      columns: [
        { key: "parameter", label: "Paramètre technique", align: "left" },
        { key: "customRange", label: "Plage disponible", align: "right" },
        { key: "toleranceStd", label: "Norme de tolérance", align: "left" },
        { key: "options", label: "Options de conception", align: "left" },
      ],
      rows: [
        {
          parameter: "Ouverture utile",
          customRange: "1 µm – 25 mm",
          toleranceStd: "ISO 9044 / ASTM E2016",
          options: "Ouvertures carrées, rectangulaires ou seuils micrométriques",
        },
        {
          parameter: "Nombre de mailles",
          customRange: "1 – 635 Mesh",
          toleranceStd: "ASTM E2016",
          options: "Tissage carré équilibré ou rapports asymétriques",
        },
        {
          parameter: "Diamètre de fil",
          customRange: "0,020 – 2,0 mm",
          toleranceStd: "DIN 4189 / ASTM A580",
          options: "Nuances austénitiques 304, 316 et 316L",
        },
        {
          parameter: "Largeur utile",
          customRange: "Jusqu'à 3 000 mm",
          toleranceStd: "Standard d'usine",
          options: "Bandes refendues dès 10 mm jusqu'aux rouleaux de 3 000 mm",
        },
      ],
      caption: "Certificats de matière et contrôles PMI disponibles selon les exigences de la commande.",
    },
    features: {
      heading: "Ingénierie & compétences de parachèvement",
      intro: "Services industriels intégrés pour équipementiers et constructeurs.",
      items: [
        {
          title: "Renforcement anisotrope",
          text: "Fils de chaîne renforcés pour supporter les efforts de traction dans les filtres à bande continue sans réduire le débit transversal.",
        },
        {
          title: "Finition des lisières",
          text: "Découpe nette, refendage ultrasonique ou bordures métalliques soudées pour éviter l'effilochage lors du montage.",
        },
        {
          title: "Découpe et emboutissage",
          text: "Outillages dédiés pour fabriquer disques simples ou multicouches sans bavures périphériques.",
        },
        {
          title: "Assemblage soudé",
          text: "Soudure par résistance et roulage pour livrer des cartouches, paniers et crépines prêts à l'emploi.",
        },
      ],
    },
    related: {
      heading: "Toiles spéciales associées",
      intro: "Comparez les toiles sur mesure avec les autres modèles de notre gamme de tissage spécial.",
      cards: [
        {
          title: "Toiles métalliques spéciales",
          text: "Vue d'ensemble de notre gamme technique pour procédés de filtration et de séparation complexes.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Catégorie mère",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Toile tissée inox de haute précision pour filtration spéciale",
          },
        },
        {
          title: "Tissage 5 cadres (Five-Heddle)",
          text: "Armure satin à face lisse pour l'évacuation rapide du gâteau sans colmatage.",
          to: "/FiveHeddleWeavePage",
          badge: "Évacuation gâteau",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Schéma structurel de toile métallique 5 cadres",
          },
        },
        {
          title: "Toile tissée renforcée",
          text: "Toiles pré-ondulées en fils épais pour carrières, granulats et environnements fortement abrasifs.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Service lourd",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Toile métallique tissée pré-ondulée pour service lourd",
          },
        },
      ],
    },
    cta: {
      id: "custom-woven-cta",
      eyebrow: "Partenariat technique OEM",
      heading: "Concevoir votre toile métallique sur mesure",
      lead: "Transmettez-nous vos conditions de procédé, plans ou échantillons pour étudier la faisabilité technique et établir un devis.",
      secondaryLabel: "Voir les toiles spéciales",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Échantillons, bandes refendues et séries complètes livrables sur demande.",
    },
  },

  pt: {
    breadcrumbKey: "CustomWovenMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Telas metálicas especiais",
      standardBadge: "ASTM E2016 / ISO 9044",
      heading: "Tela tecida sob medida",
      lead: "Engenharia de tecidos metálicos configurando proporções especiais entre urdume e trama, ligas selecionadas e acabamentos dedicados para fabricantes OEM.",
      secondaryCta: {
        label: "Telas especiais geral",
        to: "/SpecialtyWovenMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Estrutura de trama de malha de arame de aço inoxidável personalizada",
      },
      cardNote: "Engenharia para fabricantes OEM",
      cardLink: {
        label: "Enviar desenho ou especificação",
        to: "/ContactPage",
      },
      specs: [
        { label: "Faixa de abertura", value: "1 µm – 25 mm" },
        { label: "Contagem de malha", value: "1 – 635 Mesh" },
        { label: "Diâmetro do arame", value: "0,020 – 2,0 mm" },
        { label: "Largura máxima", value: "Até 3.000 mm" },
      ],
    },
    overview: {
      eyebrow: "Fabricação personalizada",
      heading: "Engenharia sob medida: do ajuste do tear ao componente filtrante acabado",
      paragraphs: [
        "Especificações padronizadas de catálogo nem sempre atendem aos requisitos mecânicos, hidráulicos ou de espaço de equipamentos industriais específicos. Nosso programa de fabricação personalizada possibilita o controle direto sobre diâmetros de arame, aberturas retangulares e acabamentos de ourela.",
        "Seja para reforçar a resistência à tração em filtros de esteira através de arames de urdume mais grossos ou para estabelecer aberturas especiais que otimizem tempo de contato e perda de carga, nossos teares operam calibrados segundo as normas ISO 9044 e ASTM E2016.",
        "Além do fornecimento em bobinas, oferecemos operações integradas de corte longitudinal em tiras estreitas, corte a laser, estampagem de discos circulares, solda por resistência multicamadas e molduras metálicas prontas para montagem.",
      ],
      keyPoints: [
        "Proporções sob medida urdume/trama para reforço mecânico direcional",
        "Confeccionada em aços inoxidáveis austeníticos certificados AISI 304, 316 e 316L",
        "Processamento completo: corte longitudinal, estamparia de peças e elementos soldados",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/specialty-woven-mesh.webp",
        alt: "Inspeção visual e metrológica de rolo de tela metálica sob medida",
      },
    },
    specTable: {
      heading: "Capacidade de fabricação sob medida",
      intro: "Limites dimensionais e parâmetros técnicos disponíveis para encomendas personalizadas.",
      columns: [
        { key: "parameter", label: "Parâmetro técnico", align: "left" },
        { key: "customRange", label: "Faixa disponível", align: "right" },
        { key: "toleranceStd", label: "Norma de tolerância", align: "left" },
        { key: "options", label: "Opções de configuração", align: "left" },
      ],
      rows: [
        {
          parameter: "Abertura de malha",
          customRange: "1 µm – 25 mm",
          toleranceStd: "ISO 9044 / ASTM E2016",
          options: "Quadradas, retangulares ou limites micrométricos específicos",
        },
        {
          parameter: "Contagem de malha",
          customRange: "1 – 635 Mesh",
          toleranceStd: "ASTM E2016",
          options: "Trama quadrada simétrica ou contagens assimétricas",
        },
        {
          parameter: "Diâmetro do arame",
          customRange: "0,020 – 2,0 mm",
          toleranceStd: "DIN 4189 / ASTM A580",
          options: "Aços inoxidáveis AISI 304, 316 e 316L",
        },
        {
          parameter: "Largura de tecelagem",
          customRange: "Até 3.000 mm",
          toleranceStd: "Padrão de fábrica",
          options: "Fitas cortadas a partir de 10 mm até bobinas de 3.000 mm",
        },
      ],
      caption: "Relatórios de ensaio de material e conferência PMI disponíveis mediante solicitação no pedido.",
    },
    features: {
      heading: "Engenharia e processamento integrado",
      intro: "Serviços industriais adaptados para fabricantes de equipamentos.",
      items: [
        {
          title: "Reforço anisotrópico",
          text: "Arames de urdume reforçados para resistir a esforços de tração contínua em correias de esteira filtrante.",
        },
        {
          title: "Acabamento de bordas",
          text: "Corte limpo, refilamento ultrassônico ou fitas metálicas soldadas para prevenir o desfiamento na instalação.",
        },
        {
          title: "Estampagem de precisão",
          text: "Matrizes especiais para fabricação de discos simples ou multicamadas e formatos geométricos sem rebarbas.",
        },
        {
          title: "Montagens soldadas",
          text: "Soldagem por resistência e calandragem para fornecimento de cartuchos cilíndricos e cestos filtrantes.",
        },
      ],
    },
    related: {
      heading: "Telas especiais correlatas",
      intro: "Compare os desenvolvimentos sob medida com as tipologias de nossa linha de telas especiais.",
      cards: [
        {
          title: "Telas metálicas tecidas especiais",
          text: "Visão ampla de tecidos de arame desenvolvidos para ambientes mecânicos e químicos severos.",
          to: "/SpecialtyWovenMeshPage",
          badge: "Linha principal",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/selvage-woven-mesh.jpg",
            alt: "Tela tecida de aço inoxidável de precisão especial",
          },
        },
        {
          title: "Tecelagem de 5 liços",
          text: "Trama de cetim com face plana de baixo atrito para desprendimento rápido de torta sólida.",
          to: "/FiveHeddleWeavePage",
          badge: "Descarga de torta",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/Twilled-Weave-5-bonded-wire-mesn1.webp",
            alt: "Diagrama estrutural de malha de cinco liços",
          },
        },
        {
          title: "Tela tecida de alta resistência",
          text: "Telas pré-onduladas com fios espessos para aplicações abrasivas em mineração e britagem.",
          to: "/HeavyDutyWovenMeshPage",
          badge: "Serviço pesado",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heavy-duty-crimped-Wire-Square-Mesh.webp",
            alt: "Tela de arame tecida pré-ondulada para serviço pesado",
          },
        },
      ],
    },
    cta: {
      id: "custom-woven-cta",
      eyebrow: "Cooperação técnica OEM",
      heading: "Configurar sua tela tecida personalizada",
      lead: "Envie suas condições de operação, desenhos de projeto ou amostras para análise de viabilidade e elaboração de proposta técnica.",
      secondaryLabel: "Explorar telas especiais",
      secondaryTo: "/SpecialtyWovenMeshPage",
      note: "Fornecimento de protótipos, fitas estreitas e lotes industriais sob medida.",
    },
  },
};
